import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    QueryResponse,
    Filter,
    FilterOperators,
    SortDirections,
    FilterMetadata,
    ApiQuery,
} from "~/modules/filters/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_SORT,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import {
    ASSET_CLASS_IDS,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import { VisualizationMode } from "~/modules/visualization/components/Timeseries/types";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { getCommoditiesTimeSeriesModes } from "~/modules/asset_classes/components/CommoditiesTimeSeriesCard";
import { TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/ActiveAddresses";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { DEPOSIT_INSURANCE } from "~/modules/assets/components/AssetTable/columns/DepositInsurance";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";
import {
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { REDEMPTION_DESCRIPTION } from "~/modules/assets/components/AssetTable/columns/RedemptionDescription";
import { AUDITOR_COLUMN } from "~/modules/assets/components/AssetTable/columns/ServiceProviders";
import { TRAILING_30_DAY_TRANSFER_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferCount";
import { TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferVolume";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const COMMODITY_ASSETS_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.commodities,
    sort: ASSET_CLASS_PAGE_TO_SORT.commodities,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

const SEO_TITLE = "Tokenized Commodities";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.commodities;

// // // //

const COMMODITY_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    AUDITOR_COLUMN,
    DEPOSIT_INSURANCE,
    REDEMPTION_DESCRIPTION,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
    TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
    TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
    TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all commodity queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.commodities;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        rawLeagueTableTabs,
        distributedAggregates,
        representedAggregates,
        transferVolumeData,
        activeAddressesData,
        holdersData,
    ] = await Promise.all([
        queryAssets({
            query: {
                ...COMMODITY_ASSETS_QUERY,
                // NOTE - this `fields` array MUST be kept in lockstep with the data that's actually used on this page
                // If adding a new column to STABLECOIN_TABLE_COLUMNS, add the corresponding dependent fields to this array
                // fields: [
                //     AssetColumns.id,
                //     AssetColumns.name,
                //     AssetColumns.slug,
                //     AssetColumns.ticker,
                //     AssetColumns.colorHex,
                //     AssetColumns.iconUrl,
                //     "price_dollar.val",
                //     "price_dollar.chg_30d_pct",
                //     AssetColumns.regulatoryFramework,
                //     AssetColumns.underlyingAssetClasses,
                //     AssetColumns.regulatoryFramework,
                //     AssetColumns.governingBody,
                //     "sp_rating",
                //     "issuer",
                //     "are_accounts_segregated",
                //     "accounts_segregated_description",
                //     "custody_description",
                //     "are_holdings_bankruptcy_remote",
                //     "holdings_bankruptcy_remote_description",
                //     "custody_description",
                //     "transparency_url",
                //     "transparency_type",
                //     "transparency_description",
                //     "pegged_asset",
                //     "sector_names",
                //     "legal_structure__document_name",
                //     "legal_structure__document_url",
                //     "legal_structure__document_description",
                //     "primary_market_pegged_asset",
                //     "primary_market",
                //     // Asset Metrics
                //     "net_asset_value_dollar.val",
                //     "net_asset_value_dollar.chg_30d_pct",
                //     "bridged_token_value_dollar.val",
                //     "bridged_token_value_dollar.chg_30d_pct",
                //     "trailing_30_day_active_addresses_count.val",
                //     "trailing_30_day_active_addresses_count.chg_30d_pct",
                //     "holding_addresses_count.val",
                //     "holding_addresses_count.chg_30d_pct",
                //     "trailing_30_day_transfer_count.val",
                //     "trailing_30_day_transfer_count.chg_30d_pct",
                //     "trailing_30_day_transfer_volume.val",
                //     "trailing_30_day_transfer_volume.chg_30d_pct",
                //     // Asset Auditor
                //     "auditor.id",
                //     "auditor.name",
                //     "auditor.slug",
                //     "auditor.icon_url",
                //     // Token Fields
                //     "tokens.id",
                //     "tokens.name",
                //     "tokens.address",
                //     "tokens.tokenization_type_id",
                //     "tokens.total_supply_token.val",
                //     // Token.network
                //     "tokens.network_name",
                //     "tokens.network.id",
                //     "tokens.network.name",
                //     "tokens.network.slug",
                //     "tokens.network.icon_url",
                //     "tokens.network.color_hex",
                //     // Token.platform
                //     "tokens.platform.id",
                //     "tokens.platform.name",
                //     "tokens.platform.slug",
                //     "tokens.platform.icon_url",
                //     "tokens.platform.color_hex",
                //     // Token metrics
                //     "tokens.bridged_token_value_dollar.val",
                //     "tokens.bridged_token_value_dollar.chg_30d_pct",
                //     "tokens.trailing_30_day_active_addresses_count.val",
                //     "tokens.trailing_30_day_active_addresses_count.chg_30d_pct",
                //     "tokens.holding_addresses_count.val",
                //     "tokens.holding_addresses_count.chg_30d_pct",
                //     "tokens.trailing_30_day_transfer_count.val",
                //     "tokens.trailing_30_day_transfer_count.chg_30d_pct",
                //     "tokens.trailing_30_day_transfer_volume.val",
                //     "tokens.trailing_30_day_transfer_volume.chg_30d_pct",
                // ],
            },
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.commodities,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--commodities",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.peggedAssets,
                LeagueTableGroupTypes.networks,
            ],
            labelOverrides: {
                [LeagueTableGroupTypes.peggedAssets]: {
                    key: "underlying_stock",
                    label: "Underlying Stock",
                    groupColumnLabel: "Stock",
                    enableEntityLinks: false,
                },
            },
        }),
        // Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--commodities--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--commodities--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--commodities--monthly-transfer-volume",
            measureID: MEASURE_IDS.Trailing30DayTransferVolumeDollar,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--commodities--monthly-active-addresses",
            measureID: MEASURE_IDS.Trailing30DayActiveAddressesCount,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--commodities--holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
    ]);

    const renameUnderlyingTab = (
        tab: (typeof rawLeagueTableTabs.all)[number],
    ) => {
        if (tab.key === "underlying_stock") {
            return { ...tab, content: { ...tab.content, label: "Underlying" } };
        }
        return tab;
    };

    const leagueTableTabs: LeagueTableTabsByDistributionType = {
        all: rawLeagueTableTabs.all.map(renameUnderlyingTab),
        distributed: rawLeagueTableTabs.distributed.map(renameUnderlyingTab),
        represented: rawLeagueTableTabs.represented.map(renameUnderlyingTab),
    };

    // // // //

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Transfer Volume",
            tooltip:
                "Sum of value transferred across all tracked commodity tokens in the last 30 days",
            type: AggregateValueTypes.dollarCompact,
            value: transferVolumeData.value,
            percentChange: {
                value: transferVolumeData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Active Addresses",
            tooltip:
                "Count of addresses that have transferred a tracked commodity token in the 30 days",
            type: AggregateValueTypes.count,
            value: activeAddressesData.value,
            percentChange: {
                value: activeAddressesData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Holders",
            tooltip: "The number of addresses that hold commodity tokens",
            type: AggregateValueTypes.countCompact,
            value: holdersData.value,
            percentChange: {
                value: holdersData.change30D,
                interval: "30d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata
        .filter((f) => {
            return [
                AssetColumns.protocolIDs,
                AssetColumns.networkIDs,
                AssetColumns.tokenizationTypeIDs,
                // AssetColumns.peggedAssetID,
            ].includes(f.field as AssetColumns);
        })
        .map((f) => {
            if (f.field === AssetColumns.tokenizationTypeIDs) {
                return {
                    ...f,
                    content: {
                        ...f.content,
                        label: "Tokenization Type",
                    },
                };
            }

            // if (f.field === AssetColumns.peggedAssetID) {
            //     return {
            //         ...f,
            //         content: {
            //             ...f.content,
            //             label: "Pegged Commodity",
            //         },
            //     };
            // }

            return f;
        });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            aggregates,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function CommoditiesPage(props: {
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    aggregates: Array<AggregateDisplayProps>;
}) {
    const { listQueryResponse, filterMetadata, leagueTableTabs, aggregates } =
        props;

    const modes: VisualizationMode[] = getCommoditiesTimeSeriesModes({
        filters: [
            {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: TokenTimeseriesColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.Commodities,
                    },
                ],
            },
        ],
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        enableGroupByControls: true,
    });

    return (
        <PageTrack route={Routes.commodities}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.commodities)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.commodities),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized commodities, tokenized gold, PAXG, XAUT, precious metals, commodity tokens, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.commodities}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.commodities,
                    },
                ]}
            />

            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.commodities} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.commodities}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={COMMODITY_ASSETS_QUERY}
                        header={{
                            label: "Tokenized Commodities",
                            loading: false,
                            value: `${listQueryResponse.pagination.resultCount.toLocaleString()} Total`,
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Commodity Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            title: "Tokenized Commodity Value",
                            leagueTableData: leagueTableTabs,
                            defaultGroupBy: LeagueTableGroupTypes.platforms,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.peggedAssets,
                                LeagueTableGroupTypes.networks,
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                            groupByLabelOverrides: {
                                [LeagueTableGroupTypes.peggedAssets]:
                                    "Underlying Stock",
                            },
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName: "commodity-league-table",
                            title: "Tokenized Commodity League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: COMMODITY_TABLE_COLUMNS,
                            analyticsTableName: "commodity_assets",
                            exportFilename: "commodities-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
